/**
 * Константы для генерации QR-кодов оплаты
 * На основе реквизитов из файла payment.txt
 */

export const PAYMENT_AMOUNT = 500; // Сумма платежа в рублях

export const PAYMENT_DETAILS = {
  // Основная информация о получателе
  recipient: 'УФК по Московской области (УМВД России по Ленинскому г.о. л/с 04481054220)',
  
  // Реквизиты организации
  ogrn: '1035000907589',
  inn: '5003010694',
  kpp: '500301001',
  
  // Банковские реквизиты
  bank: {
    name: 'ГУ БАНКА РОССИИ ПО ЦФО//УФК ПО МОСКОВСКОЙ ОБЛАСТИ г. Москва',
    bik: '004525987',
    treasuryAccount: '03100643000000014800', // Казначейский счет
    unifiedTreasuryAccount: '40102810845370000004', // Единый казначейский счет
  },
  
  // Коды и классификаторы
  okato: '46707000', // ОКТМО
  kbk: '18810807200010039110', // КБК
  
  // Назначение платежа
  purpose: 'Государственная пошлина за внесение сведений в единую автоматизированную информационную систему технического осмотра при оформлении диагностической карты по результатам осмотра транспортных средств',
  
  // Краткое назначение для QR-кода (ограничение по длине)
  shortPurpose: 'Госпошлина за техосмотр ТС',
};

/**
 * Генерация данных для QR-кода банковского перевода
 * Формат: ST00011|Name=...|PersonalAcc=...|BankName=...|BIC=...|CorrespAcc=...|PayeeINN=...|PayeeKPP=...|Purpose=...|Sum=...|KBK=...|OKATO=...
 */
export const generateBankTransferQR = (amount: number = PAYMENT_AMOUNT): string => {
  const amountKopecks = Math.round(amount * 100);
  
  return [
    'ST00011',
    `Name=${PAYMENT_DETAILS.recipient}`,
    `PersonalAcc=${PAYMENT_DETAILS.bank.treasuryAccount}`,
    `BankName=${PAYMENT_DETAILS.bank.name}`,
    `BIC=${PAYMENT_DETAILS.bank.bik}`,
    `CorrespAcc=${PAYMENT_DETAILS.bank.unifiedTreasuryAccount}`,
    `PayeeINN=${PAYMENT_DETAILS.inn}`,
    `PayeeKPP=${PAYMENT_DETAILS.kpp}`,
    `Purpose=${PAYMENT_DETAILS.shortPurpose}`,
    `Sum=${amountKopecks}`,
    `KBK=${PAYMENT_DETAILS.kbk}`,
    `OKATO=${PAYMENT_DETAILS.okato}`
  ].join('|');
};

/**
 * Генерация данных для СБП QR-кода
 * Формат: https://qr.nspk.ru/AA04[код_банка][сумма_в_копейках][назначение]
 */
export const generateSBPQR = (amount: number = PAYMENT_AMOUNT): string => {
  const bankCode = PAYMENT_DETAILS.bank.bik;
  const amountKopecks = Math.round(amount * 100);
  const encodedPurpose = encodeURIComponent(PAYMENT_DETAILS.shortPurpose);
  
  return `https://qr.nspk.ru/AA04${bankCode}${amountKopecks.toString().padStart(10, '0')}${encodedPurpose}`;
};

/**
 * Полная информация о платеже для отображения пользователю
 */
export const getPaymentInfo = (amount: number = PAYMENT_AMOUNT) => ({
  amount,
  amountKopecks: Math.round(amount * 100),
  recipient: PAYMENT_DETAILS.recipient,
  inn: PAYMENT_DETAILS.inn,
  kpp: PAYMENT_DETAILS.kpp,
  ogrn: PAYMENT_DETAILS.ogrn,
  bank: PAYMENT_DETAILS.bank,
  okato: PAYMENT_DETAILS.okato,
  kbk: PAYMENT_DETAILS.kbk,
  purpose: PAYMENT_DETAILS.purpose,
  shortPurpose: PAYMENT_DETAILS.shortPurpose,
  qrData: {
    bankTransfer: generateBankTransferQR(amount),
    sbp: generateSBPQR(amount)
  }
});

/**
 * Валидация суммы платежа
 */
export const validatePaymentAmount = (amount: number): boolean => {
  return amount > 0 && amount <= 999999 && Number.isInteger(amount);
};

/**
 * Форматирование суммы для отображения
 */
export const formatPaymentAmount = (amount: number): string => {
  return `${amount.toLocaleString('ru-RU')} ₽`;
};

/**
 * Константы для UI
 */
export const PAYMENT_UI = {
  title: 'Оплата государственной пошлины за техосмотр',
  description: 'Оплата пошлины за внесение сведений в единую автоматизированную информационную систему технического осмотра',
  amountLabel: 'Сумма к оплате',
  recipientLabel: 'Получатель',
  purposeLabel: 'Назначение платежа',
  bankLabel: 'Банк получателя',
  innLabel: 'ИНН',
  kppLabel: 'КПП',
  ogrnLabel: 'ОГРН',
  bikLabel: 'БИК',
  kbkLabel: 'КБК',
  okatoLabel: 'ОКТМО',
  treasuryAccountLabel: 'Казначейский счет',
  unifiedTreasuryAccountLabel: 'Единый казначейский счет'
} as const;
